import Link from "@material-ui/core/Link";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

import {
  ACCEPTABLE_USE_POLICY_PAGE_ROUTE,
  IMAGE_RIGHTS_MANAGEMENT_TEMPLATE_PAGE_ROUTE,
} from "../../../Routes";
import BulletPoint from "../../components/BulletPoint/BulletPoint";
import useStyles from "./ServicesAgreementPage.styles";

export default function ServicesAgreementPage() {
  const classes = useStyles();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "EcoShot Image Hub - Services Agreement";
  }, []);

  return (
    <div>
      <Paper className={classes.content}>
        <Typography variant="h5" component="h2" className={classes.heading}>
          EcoShot Services Agreement
        </Typography>
        <div className={classes.metadata}>
          <Typography color="textSecondary" variant="body2">
            Version 4
          </Typography>
          <Typography color="textSecondary" variant="body2">
            Effective: 27 June 2023
          </Typography>
        </div>
        <div className={classes.mainContent}>
          <Typography variant="body2">
            The terms below describe your rights and responsibilities when using
            EcoShot. Please read them carefully. We are grateful you are here.
          </Typography>
          <Typography variant="body2">
            This Services Agreement ("<b>SA</b>") is made between:
            <br />
            (1) Metail Limited, whose registered company number is 06442935 and
            whose registered office is 22 Station Road, Cambridge, CB1 2JD, U.K.
            (the "<b>Supplier</b>"); and
            <br />
            (2) "<b>Customer</b>" is the organisation that you represent when
            entering into the Agreement. When registering an account using a
            corporate email domain, your organization is Customer.
          </Typography>
          <Typography variant="body2">
            Customer wishes to engage Supplier to supply the Services and
            licence rights to use the Product on the terms and conditions set
            out in this SA. This SA (or, if applicable, your written agreement
            with us), along with Acceptable Use Policy and any Order Forms
            together form a binding "<b>Agreement</b>" between Customer and
            Supplier.
          </Typography>
          <Typography variant="body2">
            If you register an account, invite users to an EcoShot group (i.e.,
            a digital space where a group of users may access EcoShot images
            requested by your organisation), invite users to that group, or use
            or allow use of the Product after being notified of a change to this
            SA, you acknowledge your understanding of the then-current Agreement
            and entrance into the Agreement on behalf of Customer.
          </Typography>
          <Typography variant="body2">
            Please make sure you either (i) have the necessary authority or (ii)
            have the approval from an individual at Customer with the necessary
            authority to enter into the Agreement on behalf of Customer. By
            registering an account, each party represents and warrants to the
            other that it has full power and authority to enter into this
            Agreement and that it is binding upon such party and enforceable in
            accordance with its terms. You further warrant and represent that
            you have the authority to procure your Affiliates compliance with
            the terms of this Agreement. Please only register once you have
            secured this authority.
          </Typography>
          <BulletPoint bullet={<b>1</b>}>
            <Typography variant="body2">
              <b>INTERPRETATION</b>
            </Typography>
          </BulletPoint>
          <BulletPoint bullet="1.1">
            <Typography variant="body2" gutterBottom>
              In this Agreement, unless the context otherwise requires, the
              following words have the following meanings:
            </Typography>
            <Typography variant="body2" gutterBottom>
              "<b>Affiliates</b>" means any entity which directly or indirectly
              controls, is controlled by, or is under common control with a
              party to this Agreement. For purposes of this definition, control
              means direct or indirect ownership or control of more than 50% of
              the voting interests of the subject entity.
            </Typography>
            <Typography variant="body2" gutterBottom>
              "<b>Acceptable Use Policy</b>" means the policy included at
              Schedule 3 as updated and amended by the Supplier from time to
              time;
            </Typography>
            <Typography variant="body2" gutterBottom>
              "<b>Code</b>" means any object code and/or source code created or
              owned by the Supplier and used in the provision of the Services;
            </Typography>
            <Typography variant="body2" gutterBottom>
              "<b>Combined Materials</b>" means all materials created or
              developed by, or on behalf of, the Supplier in connection with
              this Agreement (including without limitation the combined EcoShot
              images), whether included in the Product or otherwise, excluding
              all Releases and including, but not limited to: the graphical user
              interface consisting of any textual, motion designs, graphical and
              design elements, including, but not limited to, the positions of
              such elements on the screen and the "look and feel" of the Product
              and any animation and any materials derived or adapted from the
              Supplier Materials and/or Creative Materials;
            </Typography>
            <Typography variant="body2" gutterBottom>
              "<b>Combined Materials Term</b>" means the period the Customer
              agrees to this Services Agreement in order to use the Product to
              create Combined Materials plus three (3) years from the end of the
              Term. For Trial Services, the Combined Materials Term will be the
              period during which Customer has access to the Trial Services;
            </Typography>
            <Typography variant="body2" gutterBottom>
              "<b>Commencement Date</b>" means the initial start date of the
              Term as specified in the Order Forms;
            </Typography>
            <Typography variant="body2" gutterBottom>
              "<b>Confidential Information</b>" has the meaning set out in
              clause 9.1;
            </Typography>
            <Typography variant="body2" gutterBottom>
              "<b>Creative Materials</b>" means any audio, visual, graphic,
              textual elements or any other materials or information provided by
              the Customer to the Supplier including but not limited to any
              audio-visual content, code, software, creative concepts, trade
              marks, logos and/or character designs, likenesses and shapes,
              film, model consents, Customer Model Images and footage;
            </Typography>
            <Typography variant="body2" gutterBottom>
              "<b>Customer Model Agency</b>" means the agency, photographer,
              model or other third party that supplied models to the Customer
              for the creation of Customer Model Images and from whom model
              consents need to be obtained;
            </Typography>
            <Typography variant="body2" gutterBottom>
              "<b>Customer Model Images</b>" means the images and scans of
              models provided by the Customer for use in conjunction with the
              Product by Customer under the terms of this Agreement;
            </Typography>
            <Typography variant="body2" gutterBottom>
              "<b>Dispute</b>" has the meaning set out in clause 11.1;
            </Typography>
            <Typography variant="body2" gutterBottom>
              "<b>Dispute Notice</b>" has the meaning set out in clause 11.1.1;
            </Typography>
            <Typography variant="body2" gutterBottom>
              "<b>Fees</b>" means the amount Customer must pay to the Supplier
              to supply the Services and for licence rights to use the Product.
            </Typography>
            <Typography variant="body2" gutterBottom>
              "<b>Improvements</b>" means any enhancements, modifications,
              improvements, updates or derivative works, including any related
              intellectual property that comes into existence during the Term,
              made by or on behalf of Customer to the Product;
            </Typography>
            <Typography variant="body2" gutterBottom>
              "<b>Intellectual Property Rights</b>" or "<b>IPR</b>" means
              patents, know-how, registered and unregistered trademarks and
              service marks (including any trade, brand or business names),
              domain names, registered designs, design rights, utility models,
              database rights, copyright (including all such rights in computer
              software, information, know-how and techniques in whatever form
              held and any databases), in each case for the full period thereof
              and all extensions and renewals thereof, applications for any of
              the foregoing and the right to apply for any of the foregoing in
              any part of the world and any similar rights situated in any
              country;
            </Typography>
            <Typography variant="body2" gutterBottom>
              "<b>Installation Schedule</b>" means Schedule 4 to this Agreement;
            </Typography>
            <Typography variant="body2" gutterBottom>
              "<b>Model Images</b>" means the images of models provided by the
              Supplier for use in conjunction with the Product by Customer under
              the terms of this Agreement;
            </Typography>
            <Typography variant="body2" gutterBottom>
              "<b>Open Source Code</b>" means any software licensed under any
              form of open-source licence which meets the Open Source
              Initiative's Open Source Definition
              (http://www.opensource.org/docs/definition.php) or any library or
              code licensed from time to time under the General Public Licence
              (as described by the Free Software Foundation and set out at
              http://www.gnu.org/licenses/gpl.html), or anything similar;
            </Typography>
            <Typography variant="body2" gutterBottom>
              "<b>Order Forms</b>" means a Supplier-approved form, invoice or
              online Services interface "check-out" process by which Customer
              agrees to procure the Services and licence to use the Product.
            </Typography>
            <Typography variant="body2" gutterBottom>
              "<b>Patents</b>" means the patents owned by or licensed to the
              Supplier as listed at the following URL: www.metail.com;
            </Typography>
            <Typography variant="body2" gutterBottom>
              "<b>Product</b>" means the product for which the Supplier is
              engaged to provide to Customer as set out in Schedule 2 (and as
              updated and amended from time to time);
            </Typography>
            <Typography variant="body2" gutterBottom>
              "<b>Purpose</b>" means the use of the Product by Customer as set
              out in Schedule 2;
            </Typography>
            <Typography variant="body2" gutterBottom>
              "<b>Release</b>" means any upgrades and enhancements to the
              Product;
            </Typography>
            <Typography variant="body2" gutterBottom>
              "<b>Services</b>" means the reasonable maintenance and support
              services provided in relation to the Product and performed by the
              Supplier under this Agreement for the Term;
            </Typography>
            <Typography variant="body2" gutterBottom>
              "<b>Supplier Materials</b>" means those items (including but not
              limited to prototypes) comprising of any Code, inventions,
              subroutines, techniques and tools which are provided in connection
              with Services and which are embodied in the Product, and which
              were created by the Supplier excluding the Combined Materials and
              including as Applicable the Patents and including, but not limited
              to Releases, Supplier Model Images, proprietary platforms,
              applications, and software owned by the Supplier, wireframes,
              prototypes, animation and background graphics drivers, audio
              drivers, menuing systems, operating systems, multi-tasking kernels
              and other general purpose routines and drivers;
            </Typography>
            <Typography variant="body2" gutterBottom>
              "<b>Supplier Model Images</b>" means the images and scans of
              models provided by the Supplier for use in conjunction with the
              Product by Customer under the terms of this Agreement;
            </Typography>
            <Typography variant="body2" gutterBottom>
              "<b>Term</b>" means the initial term for the Supplier to supply
              the Services and for licence rights to use the Product, as
              specified in the Order Forms, and each subsequent renewal term (if
              any). For Trial Services, the Term will be the period during which
              Customer has access to the Trial Services;
            </Typography>
            <Typography variant="body2" gutterBottom>
              "<b>Territory</b>" is defined as all countries of the world;
            </Typography>
            <Typography variant="body2" gutterBottom>
              "<b>Third Party Materials</b>" means items comprising of the
              source code, object code inventions, subroutines, software
              programs, designs, techniques, tools and other materials which are
              included by the Supplier or used by the Supplier in connection
              with the Supplier’s development and design for the user experience
              for the Product that are licensed by the Supplier from third
              parties including, but not limited to animation and background
              graphics drivers, sound systems, music, audio drivers, menuing
              systems, operating systems, full motion video players,
              multi-tasking kernels and other general purpose routines and
              drivers which are licensed or otherwise acquired from a third
              party (including without limitation Open Source Code) by the
              Supplier and any Intellectual Property Rights in the Product which
              are not owned by the Supplier;
            </Typography>
            <Typography variant="body2" gutterBottom>
              "<b>Trial Services</b>" means the Service made available by the
              Supplier on either a discounted trial or free basis; and
            </Typography>
          </BulletPoint>
          <BulletPoint bullet="1.2">
            <Typography variant="body2">
              Clause, schedule and paragraph headings shall not affect the
              interpretation of this Agreement.
            </Typography>
          </BulletPoint>
          <BulletPoint bullet="1.3">
            <Typography variant="body2">
              A person includes a natural person, corporate or unincorporated
              body (whether or not having separate legal personality).
            </Typography>
          </BulletPoint>
          <BulletPoint bullet="1.4">
            <Typography variant="body2">
              A reference to a company shall include any company, corporation or
              other body corporate, wherever and however incorporated or
              established.
            </Typography>
          </BulletPoint>
          <BulletPoint bullet="1.5">
            <Typography variant="body2">
              Unless the context otherwise requires, words in the singular shall
              include the plural and in the plural shall include the singular.
            </Typography>
          </BulletPoint>
          <BulletPoint bullet="1.6">
            <Typography variant="body2">
              Unless the context otherwise requires, a reference to one gender
              shall include a reference to the other genders.
            </Typography>
          </BulletPoint>
          <BulletPoint bullet="1.7">
            <Typography variant="body2">
              A reference to a statute or statutory provision is a reference to
              it as amended, extended or re-enacted from time to time.
            </Typography>
          </BulletPoint>
          <BulletPoint bullet="1.8">
            <Typography variant="body2">
              A reference to a statute or statutory provision shall include all
              subordinate legislation made from time to time under that statute
              or statutory provision.
            </Typography>
          </BulletPoint>
          <BulletPoint bullet="1.9">
            <Typography variant="body2">
              A reference to writing or written includes email.
            </Typography>
          </BulletPoint>
          <BulletPoint bullet="1.10">
            <Typography variant="body2">
              Any words following the terms including, include, in particular or
              for example or any similar phrase shall be construed as
              illustrative and shall not limit the generality of the related
              general words.
            </Typography>
          </BulletPoint>
          <BulletPoint bullet="1.11">
            <Typography variant="body2">
              This Agreement incorporates its Schedules. Any reference to this
              Agreement includes the schedules. If there is any conflict between
              the clauses and the Schedules of this Agreement the clauses shall
              take precedence.
            </Typography>
          </BulletPoint>
          <BulletPoint bullet={<b>2</b>}>
            <Typography variant="body2">
              <b>DELIVERY AND INSTALLATION</b>
            </Typography>
          </BulletPoint>
          <BulletPoint bullet="2.1">
            <Typography variant="body2">
              The Supplier shall, subject to the Customer meeting its
              obligations hereunder, make reasonable efforts to deliver and
              connect the Product to the Customer in accordance with the
              Installation Schedule.
            </Typography>
          </BulletPoint>
          <BulletPoint bullet="2.2">
            <Typography variant="body2">
              Each party shall bear its own costs associated with the delivery,
              connection, maintenance and updates (including without limitation
              any Release) under the terms of this Agreement.
            </Typography>
          </BulletPoint>
          <BulletPoint bullet="2.3">
            <Typography variant="body2">
              During the Term the Supplier shall reasonably and promptly supply
              any Releases to the Customer and the Customer shall ensure that it
              promptly installs them and ensure that the Product is used only
              once any current Release has been installed. The Supplier can
              accept no liability to the extent that the Product or Services are
              adversely affected by the Customer’s failure to comply with this
              clause 2.3.
            </Typography>
          </BulletPoint>
          <BulletPoint bullet={<b>3</b>}>
            <Typography variant="body2">
              <b>CUSTOMER ROLES AND RESPONSIBILITIES</b>
            </Typography>
          </BulletPoint>
          <BulletPoint bullet="3.1">
            <Typography variant="body2">
              The Customer shall cooperate with the Supplier’s written
              instructions throughout the Term and provide reasonable practical
              assistance to facilitate the Supplier’s delivery of the Product
              and supply of the Services.
            </Typography>
          </BulletPoint>
          <BulletPoint bullet="3.2">
            <Typography variant="body2">
              The Customer shall ensure that it promptly complies with and
              maintains any minimum technical requirements (including without
              limitation any hardware configuration or network connectivity
              requirements) as may be reasonably specified by the Supplier from
              time-to-time for the purpose of establishing connectivity of the
              Product (including any Releases) and supplying the Services.{" "}
            </Typography>
          </BulletPoint>
          <BulletPoint bullet="3.3">
            <Typography variant="body2">
              The Customer shall ensure that it complies with the Acceptable Use
              Policy when using the Product and any outputs. This clause 3.3 is
              expressly intended to continue in force after termination or
              expiry of this agreement and shall remain in full force and effect
              accordingly.
            </Typography>
          </BulletPoint>
          <BulletPoint bullet="3.4">
            <Typography variant="body2">
              The Customer agrees that it shall not during the Term of this
              Agreement and for a further period of 12 months thereafter
              directly or indirectly solicit or induce any officer, employee,
              agent or contractor of the Supplier to terminate their employment
              or engagement with the Supplier without the prior written consent
              of the Supplier. Nothing in this clause shall prevent the Customer
              from engaging any officer, employee, agent or contractor of the
              Supplier who responds in a non-solicited manner to any job
              advertisement which is published by the Customer.
            </Typography>
          </BulletPoint>
          <BulletPoint bullet={<b>4</b>}>
            <Typography variant="body2">
              <b>SUPPLIER ROLES AND RESPONSIBILITIES</b>
            </Typography>
          </BulletPoint>
          <BulletPoint bullet="4.1">
            <Typography variant="body2">
              The Supplier shall provide the Product and Services in accordance
              with all the provisions of this Agreement.
            </Typography>
          </BulletPoint>
          <BulletPoint bullet="4.2">
            <Typography variant="body2">
              The Supplier may, subject to clause 4.3, change at any time, with
              as much prior notice to the Customer as is reasonably practicable:
            </Typography>
            <BulletPoint bullet="4.2.1">
              <Typography variant="body2">
                the content, format or nature of Product and/or the Services;
                and
              </Typography>
            </BulletPoint>
            <BulletPoint bullet="4.2.2">
              <Typography variant="body2">
                the means of access to the Product and/or the Services.
              </Typography>
            </BulletPoint>
          </BulletPoint>
          <BulletPoint bullet="4.3">
            <Typography variant="body2">
              The Supplier will use reasonable commercial endeavours to ensure
              the continuity and availability in respect of the Product and the
              Services at all reasonable times however the Customer acknowledges
              that it has the sole responsibility for complying with its
              obligations under clause 3.2.
            </Typography>
          </BulletPoint>
          <BulletPoint bullet="4.4">
            <Typography variant="body2">
              The Supplier shall ensure that it notifies the Customer in the
              event that it amends or updates its Acceptable Use Policy.
            </Typography>
          </BulletPoint>
          <BulletPoint bullet="4.5">
            <Typography variant="body2">
              The Supplier agrees that it shall not during the Term of this
              Agreement and for a further period of 12 months thereafter,
              directly or indirectly solicit or induce any officer, employee,
              agent or contractor of the Customer to terminate their employment
              or engagement with Customer without the prior written consent of
              the Customer. Nothing in this clause shall prevent the Supplier
              from engaging any officer, employee, agent or contractor of the
              Customer who responds in a non-solicited manner to any job
              advertisement which is published by the Supplier.
            </Typography>
          </BulletPoint>
          <BulletPoint bullet={<b>5</b>}>
            <Typography variant="body2">
              <b>LICENCES & INTELLECTUAL PROPERTY RIGHTS</b>
            </Typography>
          </BulletPoint>
          <BulletPoint bullet="5.1">
            <Typography variant="body2">
              Creative Materials: Supplier acknowledges that ownership of
              (including, without limitation, ownership of all Intellectual
              Property Rights in) any Creative Materials, including (save to the
              extent that they form a part of the Combined Materials) any
              modifications or adaptations of such Creative Materials produced
              in connection with this Agreement or supplied by, or on behalf of,
              Customer to Supplier by a third party, shall remain vested in
              Customer or its licensors. Customer hereby grants Supplier a
              non-exclusive licence during the Term of this Agreement to use the
              Creative Materials solely for the purposes of providing the
              Services.
            </Typography>
          </BulletPoint>
          <BulletPoint bullet="5.2">
            <Typography variant="body2">
              In consideration of the Fees paid by the Customer to the Supplier
              and subject to the Customer complying with the Acceptable Use
              Policy:
            </Typography>
            <BulletPoint bullet="5.2.1">
              <Typography variant="body2">
                Supplier Materials (including for the avoidance of doubt without
                limitation the Product) and Third Party Materials: the Customer
                acknowledges that ownership of (including, without limitation,
                ownership of all Intellectual Property Rights in) any Supplier
                Materials is and shall remain vested in the Supplier and
                ownership of (including, without limitation, ownership of all
                Intellectual Property Rights in) any Third Party Materials shall
                remain with the third party licensor. Subject to full payment of
                the Fees, the Supplier shall automatically grant to the Customer
                a royalty-free, non-exclusive, non-transferable, revocable
                licence throughout the Territory to use, reproduce and exploit
                the Supplier Materials and to the extent that it is able the
                Third Party Materials solely for the Purpose during the Term
              </Typography>
            </BulletPoint>
            <BulletPoint bullet="5.2.2">
              <Typography variant="body2">
                Combined Materials: The Supplier hereby, subject to full payment
                of the Fees and the Customer’s continued compliance with the
                Acceptable Use Policy, grants a royalty-free, non-exclusive,
                non-transferable, licence throughout the Territory to use all
                Intellectual Property Rights in the Combined Materials during
                the Combined Materials Term. For the avoidance of doubt nothing
                in this Agreement shall serve to assign ownership of the
                Supplier Materials or any Third Party Materials.
              </Typography>
            </BulletPoint>
          </BulletPoint>
          <BulletPoint bullet="5.3">
            <Typography variant="body2">
              The Customer shall promptly notify the Supplier in writing, giving
              full particulars, if any of the following matters come to its
              attention:
            </Typography>
            <BulletPoint bullet="5.3.1">
              <Typography variant="body2">
                any infringement of any Intellectual Property Rights owned or
                licensed by either party in respect of the Combined Materials or
                the Creative Materials;
              </Typography>
            </BulletPoint>
            <BulletPoint bullet="5.3.2">
              <Typography variant="body2">
                any actual, suspected or threatened infringement of any of the
                Patents;
              </Typography>
            </BulletPoint>
            <BulletPoint bullet="5.3.3">
              <Typography variant="body2">
                any actual or threatened claim that any of Patents is invalid;
              </Typography>
            </BulletPoint>
            <BulletPoint bullet="5.3.4">
              <Typography variant="body2">
                any actual or threatened opposition to any of the Patents;
              </Typography>
            </BulletPoint>
            <BulletPoint bullet="5.3.5">
              <Typography variant="body2">
                any claim made or threatened that exploitation of any of the
                Patents infringes the rights of any third party;{" "}
              </Typography>
            </BulletPoint>
            <BulletPoint bullet="5.3.6">
              <Typography variant="body2">
                any person applies for, or is granted, a patent by reason of
                which that person may be, or has been, granted, rights which
                conflict with any of the rights granted to the Customer under
                this Agreement;
              </Typography>
            </BulletPoint>
            <BulletPoint bullet="5.3.7">
              <Typography variant="body2">
                any application is made for a compulsory licence under any
                Patent; or
              </Typography>
            </BulletPoint>
            <BulletPoint bullet="5.3.8">
              <Typography variant="body2">
                any other form of attack, charge or claim to which the Patents
                may be subject.
              </Typography>
            </BulletPoint>
          </BulletPoint>
          <BulletPoint bullet="5.4">
            <Typography variant="body2">
              In respect of any of the matters listed in clause 5.3:
            </Typography>
            <BulletPoint bullet="5.4.1">
              <Typography variant="body2">
                the Supplier shall, in its absolute discretion, decide what
                action, if any, to take;
              </Typography>
            </BulletPoint>
            <BulletPoint bullet="5.4.2">
              <Typography variant="body2">
                the Supplier shall have exclusive control over, and conduct of,
                all claims and proceedings;
              </Typography>
            </BulletPoint>
            <BulletPoint bullet="5.4.3">
              <Typography variant="body2">
                the Customer shall not make any admissions other than to the
                Supplier and shall provide the Supplier with all assistance that
                it may reasonably require in the conduct of any claims or
                proceedings; and
              </Typography>
            </BulletPoint>
            <BulletPoint bullet="5.4.4">
              <Typography variant="body2">
                the Supplier shall bear the cost of any proceedings and shall be
                entitled to retain all sums recovered in any action for its own
                account.{" "}
              </Typography>
            </BulletPoint>
          </BulletPoint>
          <BulletPoint bullet="5.5">
            <Typography variant="body2">
              The provisions of section 67(1) of the Patents Act 1977 (or
              equivalent legislation in any jurisdiction) are expressly
              excluded.{" "}
            </Typography>
          </BulletPoint>
          <BulletPoint bullet="5.6">
            <Typography variant="body2">
              Nothing in this Agreement shall constitute any representation or
              warranty that:
            </Typography>
            <BulletPoint bullet="5.6.1">
              <Typography variant="body2">
                any Patent is valid or relevant to the Customer’s business or
                products;
              </Typography>
            </BulletPoint>
            <BulletPoint bullet="5.6.2">
              <Typography variant="body2">
                any Patent (if a patent application) shall proceed to grant or,
                if granted, shall be valid; or
              </Typography>
            </BulletPoint>
            <BulletPoint bullet="5.6.3">
              <Typography variant="body2">
                the exercise by the Customer of rights granted under this
                Agreement will not infringe the rights of any person.
              </Typography>
            </BulletPoint>
          </BulletPoint>
          <BulletPoint bullet="5.7">
            <Typography variant="body2">
              The Supplier shall have the sole right, but no obligation, to
              prosecute all patent applications and maintain all patents
              covering the Product in the Territory.
            </Typography>
          </BulletPoint>
          <BulletPoint bullet="5.8">
            <Typography variant="body2">
              If either party makes, devises, discovers or otherwise acquires
              rights in any Improvement, it shall enter into good faith
              negotiations with a view to granting to the other party a right to
              use such Improvement in the Territory on terms to be agreed
              between the parties.
            </Typography>
          </BulletPoint>
          <BulletPoint bullet="5.9">
            <Typography variant="body2">
              The Customer shall not, nor directly or indirectly assist any
              other person to do or omit to do anything to diminish the rights
              of the Supplier in the Patents or impair any registration of the
              Patent.
            </Typography>
          </BulletPoint>
          <BulletPoint bullet="5.10">
            <Typography variant="body2">
              The Supplier shall have the right to terminate this Agreement
              immediately in its entirety upon written notice to Customer if
              Customer or any of its affiliates or sublicensees directly, or
              indirectly through any third party, commences any interference or
              opposition proceeding with respect to, challenges the validity or
              enforceability of, or opposes any extension of any Patent.
            </Typography>
          </BulletPoint>
          <BulletPoint bullet={<b>6</b>}>
            <Typography variant="body2">
              <b>PAYMENT</b>
            </Typography>
          </BulletPoint>
          <BulletPoint bullet="6.1">
            <Typography variant="body2">
              In consideration for the provision of the Product and the Services
              to be provided by the Supplier hereunder, the Customer shall pay
              to the Supplier the Fees. For the avoidance of doubt, if any
              payment or fee is not received by the Supplier when due, the
              Supplier reserves the right not to supply, or cease to supply, any
              or all of the Product and/or Services.
            </Typography>
          </BulletPoint>
          <BulletPoint bullet="6.2">
            <Typography variant="body2">
              The Supplier shall invoice the Customer on a monthly basis. The
              Customer shall pay the Supplier’s invoices (provided such invoices
              are in accordance with this Agreement) within 30 days of the date
              of the invoice. All invoices and all payments hereunder shall be
              in pounds sterling (£) or as otherwise agreed between the parties.
            </Typography>
          </BulletPoint>
          <BulletPoint bullet="6.3">
            <Typography variant="body2">
              All sums referred to in this Agreement are exclusive of Value
              Added Tax and any other duty or tax, which shall (if and to the
              extent applicable) be payable by the Customer.{" "}
            </Typography>
          </BulletPoint>
          <BulletPoint bullet="6.4">
            <Typography variant="body2">
              The Supplier shall be responsible for and shall account (or
              procure that account is made) for all taxes chargeable in relation
              to the provision of the Services by its employees, officers,
              agents and sub-contractors (whether chargeable on the Supplier,
              the Customer or any other person) including (without limitation)
              any income tax, National Insurance contributions and Value Added
              Tax and any equivalent taxes outside the United Kingdom to the
              relevant tax authorities.
            </Typography>
          </BulletPoint>
          <BulletPoint bullet="6.5">
            <Typography variant="body2">
              Reasonable out-of-pocket expenses and disbursements may be charged
              by the Supplier on production of reasonable evidence of
              expenditure to the Customer.
            </Typography>
          </BulletPoint>
          <BulletPoint bullet="6.6">
            <Typography variant="body2">
              The Customer shall pay the Supplier interest on any sum due under
              this Agreement, at a rate of 4% a year above the Bank of England's
              base rate from time to time (but at 4% a year for any period when
              that base rate is below 0%) from when the overdue sum became due,
              until it is paid.s
            </Typography>
          </BulletPoint>
          <BulletPoint bullet={<b>7</b>}>
            <Typography variant="body2">
              <b>SECURITY AND PASSWORDS</b>
            </Typography>
          </BulletPoint>
          <BulletPoint bullet="7.1">
            <Typography variant="body2">
              The Customer shall ensure that the Product and Supplier Materials
              are kept secure, and shall use reasonable security practices and
              systems applicable to use the Product and Supplier Materials to
              prevent, and take prompt and proper remedial action against,
              unauthorised access, copying, modification, storage, reproduction,
              display or distribution of the Product and Supplier Materials.
            </Typography>
          </BulletPoint>
          <BulletPoint bullet="7.2">
            <Typography variant="body2">
              If the Customer becomes aware of any misuse of the Product or
              Supplier Materials, or any security breach in connection with this
              Agreement that could compromise the security or integrity of the
              Product and Supplier Materials or otherwise adversely affect the
              Supplier the Customer shall, at the Customer’s expense, promptly
              notify the Supplier and fully co-operate with the Supplier to
              remedy the issue as soon as reasonably practicable.
            </Typography>
          </BulletPoint>
          <BulletPoint bullet="7.3">
            <Typography variant="body2">
              The Customer agrees to co-operate with the Supplier’s reasonable
              security investigations.
            </Typography>
          </BulletPoint>
          <BulletPoint bullet={<b>8</b>}>
            <Typography variant="body2">
              <b>WARRANTIES, LIMITATION OF LIABILITY AND IPR INDEMNITY</b>
            </Typography>
          </BulletPoint>
          <BulletPoint bullet="8.1">
            <Typography variant="body2">
              Each party represents, warrants and undertakes to the other party
              that it has the right to enter into this Agreement and it is not a
              party to any agreement, contract or understanding, which would in
              any way restrict or prohibit it from undertaking or performing any
              of its obligations hereunder in accordance with the terms and
              conditions of this Agreement.
            </Typography>
          </BulletPoint>
          <BulletPoint bullet="8.2">
            <Typography variant="body2">
              The Supplier further warrants to the Customer that:
            </Typography>
            <BulletPoint bullet="8.2.1">
              <Typography variant="body2">
                the Product shall not contain any malicious software code
                including but not limited to any"time-bombs", "worms",
                "viruses", "Trojan horses" or "data destruct keys";
              </Typography>
            </BulletPoint>
            <BulletPoint bullet="8.2.2">
              <Typography variant="body2">
                it shall obtain all necessary licences and consents and comply
                with all applicable law required to enable it to perform its
                obligations under this Agreement.
              </Typography>
            </BulletPoint>
          </BulletPoint>
          <BulletPoint bullet="8.3">
            <Typography variant="body2">
              For all events and circumstances, the Supplier’s aggregate and
              cumulative liability arising out of or relating to this Agreement
              will not exceed the amounts paid by the Customer under this
              Agreement.
            </Typography>
          </BulletPoint>
          <BulletPoint bullet="8.4">
            <Typography variant="body2">
              Subject always to clause 8.6, the Supplier shall indemnify, defend
              and hold harmless the Customer and its officers, directors, and
              employees, against any loss, liability, damage, penalty, or
              expense (including reasonable legal fees for cost of defence but
              excluding indirect and consequential loss) suffered or incurred as
              a result of any third party claim that any of the Product or
              Supplier Materials infringes any third party’s Intellectual
              Property Rights. For the avoidance of doubt this clause 8 shall
              not apply to the extent that any claim is attributable to the use
              of the Combined Materials.
            </Typography>
          </BulletPoint>
          <BulletPoint bullet="8.5">
            <Typography variant="body2">
              The Customer warrants that it holds all rights (including without
              limitation all Intellectual Property Rights) in the Creative
              Materials necessary to enable it to perform its obligations under
              this Agreement. Subject always to clause 8.6, the Customer shall
              indemnify, defend and hold harmless the Supplier and its officers,
              directors, and employees, against any loss, liability, damage,
              penalty, or expense (including reasonable legal fees for cost of
              defence but excluding indirect and consequential loss) suffered or
              incurred as a result of any third party claim that any of the
              Creative Materials infringes any third party’s Intellectual
              Property Rights.
            </Typography>
          </BulletPoint>
          <BulletPoint bullet="8.6">
            <Typography variant="body2">
              In order for a party to obtain the protection under an indemnity
              specified herein:
            </Typography>
            <BulletPoint bullet="8.6.1">
              <Typography variant="body2">
                the party seeking to rely on the indemnity shall notify the
                other as soon as possible in writing as to the nature and
                circumstances of the alleged breach; and
              </Typography>
            </BulletPoint>
            <BulletPoint bullet="8.6.2">
              <Typography variant="body2">
                the party allegedly in breach (the indemnifying party) shall
                thereafter have full conduct of any such claim or litigation;
                and
              </Typography>
            </BulletPoint>
            <BulletPoint bullet="8.6.3">
              <Typography variant="body2">
                that the party relying on the indemnity shall provide all
                reasonable assistance and at no time agree any settlement or
                make any admission of liability or do any act or issue any
                statement that is likely to have a detrimental effect on the
                other party in respect hereof.
              </Typography>
            </BulletPoint>
          </BulletPoint>
          <BulletPoint bullet={<b>9</b>}>
            <Typography variant="body2">
              <b>CONFIDENTIALITY</b>
            </Typography>
          </BulletPoint>
          <BulletPoint bullet="9.1">
            <Typography variant="body2">
              Except as provided in clause 9.4 and 10, each party undertakes to
              the other to keep confidential all information (whether provided
              in written or oral format or otherwise) concerning the business,
              customers, technology, know-how, Intellectual Property Rights,
              assets, strategy and products of the other party, which are
              received or otherwise deduced as a consequence of the discussions
              leading up to this Agreement or subsequently received or deduced
              as a consequence of the performance of this Agreement ("
              <b>Confidential Information</b>"). The parties shall not use any
              such Confidential Information for any purpose other than to
              perform its obligations under this Agreement.
            </Typography>
          </BulletPoint>
          <BulletPoint bullet="9.2">
            <Typography variant="body2">
              Each of the parties shall use the same degree of care used to
              protect their own Confidential Information (and in no event less
              than reasonable care) to prevent the unauthorised disclosure or
              use of either of the other party’s Confidential Information.
            </Typography>
          </BulletPoint>
          <BulletPoint bullet="9.3">
            <Typography variant="body2">
              The parties may disclose Confidential Information to their legal,
              financial and other business advisors (in each case in so far as
              such advisors need to know such Confidential Information) or as
              may be required by law.
            </Typography>
          </BulletPoint>
          <BulletPoint bullet="9.4">
            <Typography variant="body2">
              Clause 9.1 shall not apply to any Confidential Information which
              can be demonstrated by any of the parties as being:
            </Typography>
            <BulletPoint bullet="9.4.1">
              <Typography variant="body2">
                already in its possession prior to its receipt;
              </Typography>
            </BulletPoint>
            <BulletPoint bullet="9.4.2">
              <Typography variant="body2">
                subsequently disclosed to it lawfully by a third party who did
                not obtain such Confidential Information (directly or
                indirectly) from the other party; or
              </Typography>
            </BulletPoint>
            <BulletPoint bullet="9.4.3">
              <Typography variant="body2">
                in the public domain at the time of receipt or subsequently
                other than as a result of a breach of clause 9.1.
              </Typography>
            </BulletPoint>
          </BulletPoint>
          <BulletPoint bullet={<b>10</b>}>
            <Typography variant="body2">
              <b>EXTERNAL COMMUNICATIONS</b>
            </Typography>
          </BulletPoint>
          <BulletPoint bullet="10.1">
            <Typography variant="body2">
              With the exception of making reasonable announcements referring to
              the existence of a commercial agreement between the parties (which
              for the avoidance of doubt shall be permitted), no party shall
              make, or permit any person to make, any public announcement
              concerning this Agreement without the prior written consent of the
              other party (such consent not to be unreasonably withheld or
              delayed), except as required by law, any governmental or
              regulatory authority (including any relevant securities exchange),
              any court or other authority of competent jurisdiction.
            </Typography>
          </BulletPoint>
          <BulletPoint bullet={<b>11</b>}>
            <Typography variant="body2">
              <b>DISPUTE RESOLUTION</b>
            </Typography>
          </BulletPoint>
          <BulletPoint bullet="11.1">
            <Typography variant="body2">
              If a dispute arises out of or in connection with this Agreement or
              the performance, validity or enforceability of it (a "
              <b>Dispute</b>) then the parties shall follow the procedure set
              out in this clause:
            </Typography>
            <BulletPoint bullet="11.1.1">
              <Typography variant="body2">
                either party shall give to the other written notice of the
                Dispute, setting out its nature and full particulars ("
                <b>Dispute Notice</b>), together with relevant supporting
                documents. On service of the Dispute Notice, then the Supplier
                and Customer shall attempt in good faith to resolve the Dispute;
              </Typography>
            </BulletPoint>
            <BulletPoint bullet="11.1.2">
              <Typography variant="body2">
                if no resolution is reached as a result of clause 11.1.1 are for
                any reason unable to resolve the Dispute within 7 days of
                service of the Dispute Notice, the Dispute shall be referred to
                the Managing Director of the Supplier and the Managing Director
                of the Customer of who shall attempt in good faith to resolve
                it; and
              </Typography>
            </BulletPoint>
            <BulletPoint bullet="11.1.3">
              <Typography variant="body2">
                if no resolution is reached as a result of clause 11.1.2 within
                30 days of it being referred to them, the parties will attempt
                to settle it by mediation in accordance with the CEDR Model
                Mediation Procedure. Unless otherwise agreed between the
                parties, the mediator shall be nominated by CEDR Solve. To
                initiate the mediation, a party must serve notice in writing ("
                <b>ADR notice</b>") to the other party to the Dispute,
                requesting a mediation. A copy of the ADR notice should be sent
                to CEDR Solve. The mediation will start not later than 7 days
                after the date of the ADR notice.
              </Typography>
            </BulletPoint>
          </BulletPoint>
          <BulletPoint bullet="11.2">
            <Typography variant="body2">
              The commencement of mediation shall not prevent the parties
              commencing or continuing court proceedings in relation to a
              Dispute under clause 13.10 which clause shall apply at all times.
            </Typography>
          </BulletPoint>
          <BulletPoint bullet={<b>12</b>}>
            <Typography variant="body2">
              <b>TERM AND TERMINATION</b>
            </Typography>
          </BulletPoint>
          <BulletPoint bullet="12.1">
            <Typography variant="body2">
              This Agreement shall be effective from the Commencement Date and,
              subject to earlier termination pursuant to clause 12.2 or 12.3,
              shall continue in force for the Term with the exception of clause
              5.2.2 which shall continue in force for the Combined Materials
              Term.
            </Typography>
          </BulletPoint>
          <BulletPoint bullet="12.2">
            <Typography variant="body2">
              Either party shall be entitled to terminate this Agreement on four
              (4) weeks’ notice. Any Fees due or costs incurred on work to date
              and to the end of that working week will be invoiced to the
              Customer upon termination.
            </Typography>
          </BulletPoint>
          <BulletPoint bullet="12.3">
            <Typography variant="body2">
              Either party may terminate this Agreement immediately upon written
              notice to the other party if:
            </Typography>
            <BulletPoint bullet="12.3.1">
              <Typography variant="body2">
                a party is in material breach of any of its obligations under
                this Agreement and the breach is either incapable of remedy or,
                if the breach is capable of being remedied, has continued
                un-remedied for a period of 30 days from the date on which they
                are provided notice of such breach; or
              </Typography>
            </BulletPoint>
            <BulletPoint bullet="12.3.2">
              <Typography variant="body2">
                a party has a receiver or an administrative receiver appointed
                over it or over any part of its undertaking or assets or passes
                a resolution for winding up (otherwise than for the purpose of a
                bona fide scheme of solvent amalgamation or reconstruction) or a
                court of competent jurisdiction makes an order to that effect,
                or if a party becomes subject to an administrative order or
                enters into any voluntary agreement with its creditors or ceases
                or threatens to cease to carry on business.
              </Typography>
            </BulletPoint>
          </BulletPoint>
          <BulletPoint bullet="12.4">
            <Typography variant="body2">
              Upon termination or expiry of this Agreement (howsoever caused):
            </Typography>
            <BulletPoint bullet="12.4.1">
              <Typography variant="body2">
                all rights granted to the Customer under this Agreement shall
                cease;
              </Typography>
            </BulletPoint>
            <BulletPoint bullet="12.4.2">
              <Typography variant="body2">
                the Customer shall cease all activities authorised by this
                Agreement; and{" "}
              </Typography>
            </BulletPoint>
            <BulletPoint bullet="12.4.3">
              <Typography variant="body2">
                unless otherwise agreed by the parties in writing, the Customer
                shall immediately destroy or return to the Supplier (at the
                Supplier's option) all copies of the Product (including for the
                avoidance of doubt all Supplier Materials, Third Party Materials
                and Combined Materials) then in its possession, custody or
                control and, in the case of destruction, certify to the Supplier
                that it has done so.
              </Typography>
            </BulletPoint>
          </BulletPoint>
          <BulletPoint bullet="12.5">
            <Typography variant="body2">
              The termination or expiry of this Agreement (however caused)
              shall:
            </Typography>
            <BulletPoint bullet="12.5.1">
              <Typography variant="body2">
                not affect clauses which by their nature are intended to remain
                in effect, (including, without limitation, clauses 3.3, 3.4,
                4.5, 5, 6 (in relation to the work completed to the date of
                termination), 7, 9, 10, and 13.4);
              </Typography>
            </BulletPoint>
            <BulletPoint bullet="12.5.2">
              <Typography variant="body2">
                be without prejudice to any other rights or remedies which
                either party may be entitled to under this Agreement or at law;
                and
              </Typography>
            </BulletPoint>
            <BulletPoint bullet="12.5.3">
              <Typography variant="body2">
                not affect any accrued rights or liabilities which either party
                may then have.
              </Typography>
            </BulletPoint>
          </BulletPoint>
          <BulletPoint bullet={<b>13</b>}>
            <Typography variant="body2">
              <b>GENERAL</b>
            </Typography>
          </BulletPoint>
          <BulletPoint bullet="13.1">
            <Typography variant="body2" gutterBottom>
              Amendments
            </Typography>
            <Typography variant="body2">
              As the Product evolves, the Supplier may change the SA and the
              other components of the Agreement (except any Order Forms). If the
              Supplier makes a material change to the Agreement, they will
              provide Customer with reasonable notice prior to the change taking
              effect by emailing the email addresses associated with EcoShot
              groups created for Customer. Customer can review the most current
              version of the SA at any time by visiting this page. The
              materially revised Agreement will become effective on the date set
              forth in our notice, and all other changes will become effective
              upon posting of the change. If Customer accesses or uses the
              Product after the effective date, that use will constitute
              Customer’s acceptance of any revised terms and conditions.
            </Typography>
          </BulletPoint>
          <BulletPoint bullet="13.2">
            <Typography variant="body2">Notices</Typography>
            <BulletPoint bullet="13.2.1">
              <Typography variant="body2">
                Any notice or other communication given to a party under or in
                connection with this contract shall be in writing and shall be:
              </Typography>
              <BulletPoint bullet="a)">
                <Typography variant="body2">
                  delivered by hand or by pre-paid first-class post or other
                  next working day delivery service at its registered office (if
                  a company) or its principal place of business (in any other
                  case); or
                </Typography>
              </BulletPoint>
              <BulletPoint bullet="b)">
                <Typography variant="body2">
                  for notices to the Supplier: sent by email to{" "}
                  <a href="mailto:legal@metail.com">legal@metail.com</a>{" "}
                  strictly provided it is sent from an email address submitted
                  to the Supplier during Customer registration for the Services;
                  or
                </Typography>
              </BulletPoint>
              <BulletPoint bullet="c)">
                <Typography variant="body2">
                  for notices to Customer: sent by email to email addresses
                  submitted to the Supplier during Customer registration for the
                  Services.
                </Typography>
              </BulletPoint>
            </BulletPoint>
            <BulletPoint bullet="13.2.2">
              <Typography variant="body2">
                Any notice or communication shall be deemed to have been
                received:
              </Typography>
              <BulletPoint bullet="a)">
                <Typography variant="body2">
                  if delivered by hand, on signature of a delivery receipt or at
                  the time the notice is left at the proper address; or
                </Typography>
              </BulletPoint>
              <BulletPoint bullet="b)">
                <Typography variant="body2">
                  if sent by pre-paid first-class post or other next working day
                  delivery service, at 9.00 am on the second Business Day after
                  posting or at the time recorded by the delivery service; or
                </Typography>
              </BulletPoint>
              <BulletPoint bullet="c)">
                <Typography variant="body2">
                  if sent by email upon receipt of a non-automatic read
                  confirmation.
                </Typography>
              </BulletPoint>
            </BulletPoint>
            <BulletPoint bullet="13.2.3">
              <Typography variant="body2">
                This clause does not apply to the service of any proceedings or
                other documents in any legal action or, where applicable, any
                arbitration or other method of dispute resolution. All notices
                to be given under this Agreement shall be in writing and shall
                be sent by first class recorded delivery (or express courier) to
                the address of the other party set out at the commencement of
                this Agreement (or to such other address as either party may
                notify to the other under the provisions of this sub-clause).
              </Typography>
            </BulletPoint>
          </BulletPoint>
          <BulletPoint bullet="13.3">
            <Typography variant="body2" gutterBottom>
              Assignment and Sub-Contracting
            </Typography>
            <Typography variant="body2" gutterBottom>
              The Supplier may at any time assign any of its rights or
              sub-contract any of its obligations under this Agreement provided
              that the Supplier shall be and remain fully liable to the Customer
              for the performance, including any acts or omissions of any
              subcontractor.
            </Typography>
            <Typography variant="body2">
              The Customer shall not without first gaining the prior written
              consent of the Supplier assign any of its rights or sub-contract
              any of its obligations under this Agreement.
            </Typography>
          </BulletPoint>
          <BulletPoint bullet="13.4">
            <Typography variant="body2" gutterBottom>
              Survival
            </Typography>
            <Typography variant="body2">
              Any provision of this Agreement that expressly or by implication
              is intended to come into or continue in force on or after
              termination or expiry of this Agreement shall remain in full force
              and effect.
            </Typography>
          </BulletPoint>
          <BulletPoint bullet="13.5">
            <Typography variant="body2" gutterBottom>
              No Waiver
            </Typography>
            <Typography variant="body2">
              No failure on the part of either party to exercise or to enforce
              any right given to it by this Agreement or at law or any custom or
              practice of the parties at variance with the terms of this
              Agreement shall constitute a waiver of either of the parties'
              respective rights under this Agreement or operate so as to prevent
              the exercise or enforcement of any such right at any time.
            </Typography>
          </BulletPoint>
          <BulletPoint bullet="13.6">
            <Typography variant="body2" gutterBottom>
              Force Majeure
            </Typography>
            <Typography variant="body2">
              Neither party shall be liable for breaches this Agreement which
              result from circumstances which are beyond its reasonable control
              and which could not have reasonably been foreseen, provided that
              as soon as it is reasonably practicable it gives notice to the
              other party and that if the circumstances continue for over thirty
              (30) days the party receiving notice may elect to terminate this
              Agreement on 7 days’ notice in writing.
            </Typography>
          </BulletPoint>
          <BulletPoint bullet="13.7">
            <Typography variant="body2" gutterBottom>
              Counterparts
            </Typography>
            <Typography variant="body2">
              This Agreement may be executed in counterparts and by the parties
              on separate counterparts, but shall not be effective until each
              party has executed at least one counterpart. Each counterpart,
              when executed, shall be an original of this Agreement and all
              counterparts shall together constitute one instrument.
            </Typography>
          </BulletPoint>
          <BulletPoint bullet="13.8">
            <Typography variant="body2" gutterBottom>
              Rights of Third Parties
            </Typography>
            <Typography variant="body2">
              This Agreement is not intended to and does not give any person who
              is not a party to it any right to enforce any of its provisions
              under the Contracts (Rights of Third Parties) Act 1999, or
              otherwise.
            </Typography>
          </BulletPoint>
          <BulletPoint bullet="13.9">
            <Typography variant="body2" gutterBottom>
              Entire Agreement
            </Typography>
            <Typography variant="body2" gutterBottom>
              This Agreement, as updated and amended from time to time,
              constitutes the entire agreement between the parties and
              supersedes and extinguishes all previous agreements, promises,
              assurances, warranties, representations and understandings between
              them, whether written or oral, relating to its subject matter.
            </Typography>
            <Typography variant="body2" gutterBottom>
              Each party agrees that it shall have no remedies in respect of any
              statement, representation, assurance or warranty (whether made
              innocently or negligently) that is not set out in this Agreement.
              Each party agrees that it shall have no claim for innocent or
              negligent misrepresentation based on any statement in this
              Agreement.{" "}
            </Typography>
          </BulletPoint>
          <BulletPoint bullet="13.10">
            <Typography variant="body2" gutterBottom>
              Law and Jurisdiction
            </Typography>
            <Typography variant="body2">
              This Agreement shall be governed by and construed in accordance
              with English law and the parties submit to the exclusive
              jurisdiction of the English courts.
            </Typography>
          </BulletPoint>
          <Typography
            variant="h6"
            component="h2"
            className={classes.scheduleHeading}
            gutterBottom
          >
            Schedule 2 - Product Description
          </Typography>
          <Typography variant="body2" gutterBottom>
            EcoShot is a product aimed at brands and manufacturers who use
            apparel 3D Computer Aided Design (CAD) software. It involves the
            creation of EcoShot scanatars which are posed 3D avatars generated
            from images of models and, if available, 3D body scans of the
            models. These images of models and scans might be created by the
            customer, or by Metail. The resultant EcoShot scanatars can then be
            imported into the CAD software and used to accurately simulate the
            form and dynamics of a digital 3D outfit on the digitised model in
            their digitised pose. The outfit simulation is then ray trace
            rendered to accurately simulate the optics of the digital outfit and
            combined with imagery of the photographed model to create
            true-to-life and photorealistic visualisations without the need for
            physical samples.
          </Typography>
          <Typography
            variant="h6"
            component="h2"
            className={classes.scheduleHeading}
            gutterBottom
          >
            Schedule 3 - Acceptable Use Policy
          </Typography>
          <Typography variant="body2" gutterBottom>
            The Acceptable Use Policy is available at{" "}
            <Link
              className={classes.link}
              href="#"
              onClick={(evt) => {
                evt.preventDefault();
                navigate(ACCEPTABLE_USE_POLICY_PAGE_ROUTE);
              }}
            >
              {process.env.REACT_APP_DOMAIN}/acceptable-use-policy
            </Link>
          </Typography>
          <Typography variant="body2">
            It is the definitive rulebook setting out what you can and can't do
            while using our Products and Services. Please read them carefully
            before registering an account.
          </Typography>
          <Typography
            variant="h6"
            component="h2"
            className={classes.scheduleHeading}
            gutterBottom
          >
            Schedule 4 - Installation Schedule
          </Typography>
          <Typography variant="body2" gutterBottom>
            The product installation information is available on{" "}
            <a href="https://help.ecoshotstudio.com/en/collections/7196314-ecoshot-user-guide-2023">
              help.ecoshotstudio.com
            </a>
          </Typography>
          <Typography
            variant="h6"
            component="h2"
            className={classes.scheduleHeading}
            gutterBottom
          >
            Schedule 5 - Customer Model Images: Image Rights Management Template
          </Typography>
          <Typography variant="body2">
            When providing Customer Model Images to Metail, the Customer is
            responsible for securing from the Customer Model Agency and/or the
            Photographer the appropriate rights in the Customer Model Images for
            Metail to perform its obligations under this Agreement. Metail’s
            Image Rights Management template which contains a suggested
            agreement for the Customer to establish with the Customer Model
            Agency can be found at{" "}
            <Link
              className={classes.link}
              href="#"
              onClick={(evt) => {
                evt.preventDefault();
                navigate(IMAGE_RIGHTS_MANAGEMENT_TEMPLATE_PAGE_ROUTE);
              }}
            >
              {process.env.REACT_APP_DOMAIN}/image-rights-management-template
            </Link>
          </Typography>
          <Typography variant="body2">
            Please note that Intellectual Property Rights are a complex area of
            law. If you are uncertain whether you hold the appropriate rights
            for Metail to perform its obligations under this Agreement, Metail
            advises that you obtain independent legal advice prior to providing
            Customer Model Images to Metail.
          </Typography>
        </div>
      </Paper>
    </div>
  );
}
